import React, { useState, useRef, useEffect, useCallback } from "react";
import Icon from "./Icon";

const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

const Slider = React.forwardRef(
  (
    {
      size = "xs",
      value = 0,
      min = 0,
      max = 100,
      step = 1,
      marks = false,
      valueLabel = false,
      orientation = "horizontal",
      onChange,
      disabled = false,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    const isRange = Array.isArray(value);
    const internalVal = isRange ? value : [value];

    const [activeThumb, setActiveThumb] = useState(null);
    const trackRef = useRef(null);

    const minVal = Number(min);
    const maxVal = Number(max);
    const stepVal = Number(step);

    // Ensure values are within bounds and snapped to step
    const getSnappedValue = useCallback(
      (rawVal) => {
        const clamped = clamp(Number(rawVal), minVal, maxVal);
        if (!stepVal) return clamped;
        const stepped =
          Math.round((clamped - minVal) / stepVal) * stepVal + minVal;
        return clamp(stepped, minVal, maxVal); // Re-clamp just in case of rounding errors at max
      },
      [minVal, maxVal, stepVal],
    );

    const handlePointerDown = (event, thumbIndex = null) => {
      if (disabled) return;

      // Determine which thumb to move if clicking on the track
      let targetThumb = thumbIndex;
      if (targetThumb === null) {
        if (!isRange) {
          targetThumb = 0;
        } else {
          // Find closest thumb
          if (!trackRef.current) return;
          const rect = trackRef.current.getBoundingClientRect();
          let percent;
          if (orientation === "vertical") {
            percent = clamp(1 - (event.clientY - rect.top) / rect.height, 0, 1);
          } else {
            percent = clamp((event.clientX - rect.left) / rect.width, 0, 1);
          }
          const clickVal = percent * (maxVal - minVal) + minVal;

          const dist0 = Math.abs(internalVal[0] - clickVal);
          const dist1 = Math.abs(internalVal[1] - clickVal);
          targetThumb = dist0 < dist1 ? 0 : 1;
        }
      }

      setActiveThumb(targetThumb);

      // Initial calculation jump
      handleMove(event, targetThumb);
    };

    const handleMove = useCallback(
      (event, currentThumb) => {
        if (disabled || !trackRef.current) return;

        const rect = trackRef.current.getBoundingClientRect();
        let percent;
        if (orientation === "vertical") {
          percent = clamp(1 - (event.clientY - rect.top) / rect.height, 0, 1);
        } else {
          percent = clamp((event.clientX - rect.left) / rect.width, 0, 1);
        }
        let newValue = getSnappedValue(percent * (maxVal - minVal) + minVal);

        // If range, prevent thumbs from crossing each other
        if (isRange) {
          const otherThumb = currentThumb === 0 ? 1 : 0;
          if (currentThumb === 0) {
            newValue = Math.min(newValue, internalVal[1]);
          } else {
            newValue = Math.max(newValue, internalVal[0]);
          }
        }

        if (newValue !== internalVal[currentThumb] && onChange) {
          if (isRange) {
            const newArr = [...internalVal];
            newArr[currentThumb] = newValue;
            onChange(newArr);
          } else {
            onChange(newValue);
          }
        }
      },
      [
        disabled,
        isRange,
        internalVal,
        minVal,
        maxVal,
        getSnappedValue,
        onChange,
        orientation,
      ],
    );

    useEffect(() => {
      if (activeThumb === null) return;

      const onPointerMove = (e) => handleMove(e, activeThumb);
      const onPointerUp = () => setActiveThumb(null);

      window.addEventListener("pointermove", onPointerMove);
      window.addEventListener("pointerup", onPointerUp);
      window.addEventListener("pointercancel", onPointerUp);

      return () => {
        window.removeEventListener("pointermove", onPointerMove);
        window.removeEventListener("pointerup", onPointerUp);
        window.removeEventListener("pointercancel", onPointerUp);
      };
    }, [activeThumb, handleMove]);

    // Calculations for rendering
    const getPercent = (val) => {
      const p = ((Number(val) - minVal) / (maxVal - minVal)) * 100;
      return isNaN(p) ? 0 : clamp(p, 0, 100);
    };

    const renderMarks = () => {
      if (!marks || !step) return null;
      const markEls = [];
      const propStart = orientation === "vertical" ? "bottom" : "left";

      for (let i = minVal; i <= maxVal; i += stepVal) {
        const p = getPercent(i);

        // Check if mark is inside active track to color it differently
        let isActive = false;
        if (isRange) {
          isActive = i >= internalVal[0] && i <= internalVal[1];
        } else {
          isActive = i <= internalVal[0];
        }

        // Elegantly align first and last ticks inside the pill-shaped track boundaries using the exact token
        let posStyle = `${p}%`;
        if (i === minVal) {
          posStyle = `var(--ta-comp-slider-track-shape)`;
        } else if (i === maxVal) {
          posStyle = `calc(100% - var(--ta-comp-slider-track-shape))`;
        }

        markEls.push(
          <div
            key={i}
            className={`ta-slider_mark ${isActive ? "ta-slider_mark--active" : ""}`}
            style={{ [propStart]: posStyle }}
          />,
        );
      }
      return markEls;
    };

    const renderThumb = (index) => {
      const p = getPercent(internalVal[index]);
      const isActive = activeThumb === index;
      const propStart = orientation === "vertical" ? "bottom" : "left";

      return (
        <div
          className={`ta-slider_thumb-container ${isActive ? "ta-slider_thumb-container--active" : ""}`}
          style={{ [propStart]: `${p}%` }}
          onPointerDown={(e) => {
            e.stopPropagation();
            handlePointerDown(e, index);
          }}
          tabIndex={disabled ? -1 : 0}
          role="slider"
          aria-valuemin={minVal}
          aria-valuemax={maxVal}
          aria-valuenow={internalVal[index]}
          aria-disabled={disabled}
        >
          <div className="ta-slider_thumb-hitbox" />
          <div className="ta-slider_thumb" />

          {valueLabel && (
            <div className="ta-slider_value-label">{internalVal[index]}</div>
          )}
        </div>
      );
    };

    const classes = [
      "ta-slider",
      disabled && "ta-is-disabled",
      isRange && "ta-slider--range",
      `ta-slider--${orientation}`,
      marks && "ta-slider--discrete",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Calculate track styles using left and right to automatically handle gaps
    // A gap of 8px from the thumb center is used to separate tracks
    const GAP = "8px";

    const renderTracks = () => {
      const p0 = getPercent(internalVal[0]);

      const propStart = orientation === "vertical" ? "bottom" : "left";
      const propEnd = orientation === "vertical" ? "top" : "right";

      if (isRange) {
        const p1 = getPercent(internalVal[1]);

        return (
          <>
            {/* Left Inactive Track */}
            <div
              className="ta-slider_track ta-slider_track--start"
              style={{
                [propStart]: "0%",
                [propEnd]: `calc(${100 - p0}% + ${GAP})`,
              }}
            />
            {/* Middle Active Track */}
            <div
              className="ta-slider_track-active ta-slider_track-active--middle"
              style={{
                [propStart]: `calc(${p0}% + ${GAP})`,
                [propEnd]: `calc(${100 - p1}% + ${GAP})`,
              }}
            />
            {/* Right Inactive Track */}
            <div
              className="ta-slider_track ta-slider_track--end"
              style={{
                [propStart]: `calc(${p1}% + ${GAP})`,
                [propEnd]: "0%",
              }}
            />
          </>
        );
      }

      // Single Slider
      return (
        <>
          {/* Left Active Track */}
          <div
            className="ta-slider_track-active"
            style={{
              [propStart]: "0%",
              [propEnd]: `calc(${100 - p0}% + ${GAP})`,
            }}
          />
          {/* Right Inactive Track */}
          <div
            className="ta-slider_track"
            style={{
              [propStart]: `calc(${p0}% + ${GAP})`,
              [propEnd]: "0%",
            }}
          />
        </>
      );
    };

    return (
      <div
        className={classes}
        style={style}
        {...rest}
        data-size={size}
        data-value={isRange ? internalVal.join(",") : internalVal[0]}
        onPointerDown={(e) => handlePointerDown(e, null)}
        ref={(node) => {
          trackRef.current = node;
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;
        }}
      >
        {renderTracks()}
        {renderMarks()}
        {renderThumb(0)}
        {isRange && renderThumb(1)}
      </div>
    );
  },
);

export default Slider;
